// src/components/PricingTable.jsx
export const runtime = 'experimental-edge';
import React, { useState, useEffect, useMemo } from 'react';
import useUserStore from '../store/userStore';
import DebugPanel from './SuscriptionDebugPanel';
import BillingToggle from './BillingToggle';

// Компонент для отображения спиннера загрузки
const SpinnerIcon = () => (
  <svg className="animate-spin h-5 w-5 ml-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

// CheckMark component
const CheckMark = () => (
  <svg className="text-purple-600 mr-2 mt-1 flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 12L10 17L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// X Mark component
const XMark = () => (
  <svg className="text-gray-400 mr-2 mt-1 flex-shrink-0" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PricingTable = ({ initialSubscriptionData, initialAuth }) => {
  const [isYearly, setIsYearly] = useState(false);
  const [isTrialMode, setIsTrialMode] = useState(false);
  const [trialDays, setTrialDays] = useState(14); // Default 14 days trial
  const [loading, setLoading] = useState(false);
  // Состояния загрузки для разных кнопок
  const [loadingFree, setLoadingFree] = useState(false);
  const [loadingPremium, setLoadingPremium] = useState(false);
  const [loadingPro, setLoadingPro] = useState(false);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(initialSubscriptionData);
  const [initialized, setInitialized] = useState(true); // Изменено на true, чтобы избежать показа плейсхолдера
  
  // Get user state and functions from the store
  const { 
    isLoggedIn, 
    fetchWithTokenRefresh, 
    isSubscriptionActive, 
    subscription,
    fetchUserProfile,
    setUserManually
  } = useUserStore();

  // Немедленная инициализация данных из SSR если они есть
  useEffect(() => {
    console.log('Initial data received:', { 
      initialAuth, 
      initialSubscriptionData,
      subscription
    });
    
    if (initialAuth?.isAuthenticated && initialAuth?.user) {
      setUserManually({
        ...initialAuth.user,
        isSubscriptionActive: initialSubscriptionData?.active || false,
        subscription: initialSubscriptionData?.plan || '',
      });
    }
    
    // Для отладки
    console.log('UserStore state after initialization:', {
      isLoggedIn, 
      isSubscriptionActive,
      subscription
    });
    
    // Сразу устанавливаем начальные данные о подписке
    if (initialSubscriptionData) {
      setSubscriptionStatus(initialSubscriptionData);
    }
    
    // Инициализация всегда считается завершенной
    setInitialized(true);
  }, []);

  // Fetch subscription status again if needed
  useEffect(() => {
    async function fetchDataIfNeeded() {
      if (!initialSubscriptionData && isLoggedIn) {
        await fetchSubscriptionStatus();
      }
    }
    
    fetchDataIfNeeded();
  }, [isLoggedIn]);

  // Вычисляем текущий план подписки пользователя
  const userPlan = useMemo(() => {
    // Проверяем несколькими способами, какой план у пользователя
    const fromStore = subscription?.toLowerCase() || '';
    const fromStatus = subscriptionStatus?.plan?.toLowerCase() || '';
    
    return fromStore || fromStatus || '';
  }, [subscription, subscriptionStatus]);

  // Проверяем, является ли указанный тип плана текущим планом пользователя
  const isCurrentPlan = (planType) => {
    if (!isSubscriptionActive) return false;
    
    const normalizedPlanType = planType.toLowerCase().trim();
    return normalizedPlanType === userPlan;
  };

  // Fetch current subscription status
  const fetchSubscriptionStatus = async () => {
    if (!isLoggedIn) return;
    
    try {
      setLoading(true);
      const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL;
      
      if (!backendUrl) {
        console.error('Backend URL not defined in environment variables');
        setError('Configuration error: Missing backend URL. Please contact support.');
        setLoading(false);
        return;
      }
      
      // Make the API request with error handling
      const response = await fetchWithTokenRefresh(
        `${backendUrl}/subscription/status`
      ).catch(err => {
        console.error('Network error fetching subscription:', err);
        throw new Error('Network error. Please check your connection and try again.');
      });
      
      // Handle non-OK response without throwing error
      if (!response.ok) {
        const status = response.status;
        const errorText = await response.text().catch(() => '');
        
        console.warn(`Subscription status API responded with ${status}:`, errorText);
        
        // Set subscription status to null but don't throw exception
        setSubscriptionStatus(null);
        
        // Only set user-facing error for severe failures
        if (status >= 500) {
          setError('Unable to load subscription information. The server might be experiencing issues.');
        } else if (status === 401 || status === 403) {
          setError('Your session may have expired. Please try logging in again.');
        } else {
          console.log('Non-critical subscription status error:', status);
          // Don't show error to user for 404 or other client errors
        }
        
        return;
      }
      
      // Process successful response
      const data = await response.json();
      console.log('Subscription status fetched:', data);
      setSubscriptionStatus(data);
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error in subscription status flow:', err);
      // Set a generic error message to avoid exposing technical details
      setError('Could not load your subscription information. Please try again later.');
      setSubscriptionStatus(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Get customer portal URL for subscription management
  const handleManageSubscription = async () => {
    try {
      setLoadingFree(true);
      setError(null);
      
      const response = await fetchWithTokenRefresh(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/subscription/portal`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ return_url: window.location.href }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to get customer portal URL');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe Customer Portal
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error('Portal URL error:', err);
      setError(err.message || 'Failed to access subscription management. Please try again.');
    } finally {
      setLoadingFree(false);
    }
  };

  // Handle subscription checkout
  const handleSubscribe = async (plan, interval) => {
    if (!isLoggedIn) {
      // Перенаправляем на страницу login с сохранением текущего URL
      const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${currentUrl}`;
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const trial_period_days = isTrialMode ? trialDays : null;
      const return_url = window.location.origin + '/pricing';
      
      const payload = {
        plan: plan, // 'premium' or 'pro'
        interval: interval, // 'monthly' or 'yearly' or 'daily'
        return_url: return_url,
        trial_period_days: trial_period_days
      };
      
      const response = await fetchWithTokenRefresh(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/subscription/sub_checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create subscription checkout');
      }
      
      const data = await response.json();
      
      // Redirect to Stripe checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError(err.message || 'Failed to start subscription process. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Обновленные функции-обработчики для разных планов
  const handlePremiumSubscribe = async () => {
    try {
      setLoadingPremium(true);
      if (isSubscriptionActive) {
        await handleManageSubscription();
      } else {
        await handleSubscribe('premium', isYearly ? 'yearly' : 'monthly');
      }
    } finally {
      setLoadingPremium(false);
    }
  };

  const handleProSubscribe = async () => {
    try {
      setLoadingPro(true);
      if (isSubscriptionActive) {
        await handleManageSubscription();
      } else {
        await handleSubscribe('pro', isYearly ? 'yearly' : 'monthly');
      }
    } finally {
      setLoadingPro(false);
    }
  };

  // Special handler for daily premium debug option
  const handlePremiumDaily = async () => {
    try {
      setLoadingDaily(true);
      await handleSubscribe('premium', 'daily');
    } finally {
      setLoadingDaily(false);
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    if (!isLoggedIn || !isSubscriptionActive) return;
    
    if (!confirm('Are you sure you want to cancel your subscription? You will still have access until the end of your billing period.')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchWithTokenRefresh(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/subscription/cancel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ cancel_at_period_end: true }),
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to cancel subscription');
      }
      
      // Refetch subscription status to update UI
      await fetchSubscriptionStatus();
      // Also refresh the user profile in the store
      await fetchUserProfile(true);
      alert('Your subscription has been canceled and will end at the conclusion of your billing period.');
      
    } catch (err) {
      console.error('Cancellation error:', err);
      setError(err.message || 'Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Error message if any */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-800 rounded-md">
          {error}
        </div>
      )}
      
      {/* Используем вынесенный компонент BillingToggle */}
      <BillingToggle isYearly={isYearly} setIsYearly={setIsYearly} />
      
      {/* Pricing Cards - Full width with padding only on mobile */}
      <div className="px-4 sm:px-0 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Free Plan */}
        <div className="border rounded-xl flex flex-col">
          <div className="p-6 flex flex-col flex-grow">
            <h2 className="text-4xl font-bold mb-2">Free</h2>
            <div className="mb-6">
              <span className="text-5xl font-bold">$0</span>
              <div className="mt-2">
                <p className="text-gray-700">Basic access to icons</p>
              </div>
            </div>
            
            <div className="flex mb-8">
              {/* Free plan button - показываем разные варианты в зависимости от статуса подписки */}
              <button 
                className={`bg-purple-600 text-white px-6 py-2 rounded-full font-medium ${!isSubscriptionActive ? 'opacity-60 cursor-not-allowed' : 'hover:bg-purple-700'} flex items-center justify-center`}
                disabled={!isSubscriptionActive || loadingFree}
                onClick={handleManageSubscription}
              >
                {!isLoggedIn ? 'Sign In to Subscribe' : 
                 !isSubscriptionActive ? 'Your Current Plan' : 
                 'Manage Subscription'}
                 
                {loadingFree && <SpinnerIcon />}
              </button>
            </div>
            
            <div className="flex-grow">
              <ul className="space-y-3">
                <li className="flex items-start"><CheckMark />Access to basic collections</li>
                <li className="flex items-start"><CheckMark />Free PNG format</li>
                <li className="flex items-start"><XMark />SVG vector format</li>
                <li className="flex items-start"><XMark />Icon customization</li>
                <li className="flex items-start"><XMark />Attribution required</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Premium Plan */}
        <div className="border rounded-xl flex flex-col relative overflow-hidden">
          <div className="p-6 flex flex-col flex-grow">
            <h2 className="text-4xl font-bold mb-2">Premium</h2>
            <div className="mb-6">
              <span className="text-5xl font-bold">${isYearly ? '15' : '19'}</span>
              <span className="text-purple-600 ml-1">/mo</span>
              <div className="mt-2">
                <p className="text-gray-700">
                  {isYearly ? 'Billed annually ($180/year)' : 'Billed monthly'}
                </p>
              </div>
            </div>
            
            <div className="flex mb-8">
              {/* Premium plan button - для существующих подписок открываем портал, для новых - создаем */}
              <button 
                className={`bg-purple-600 text-white px-6 py-2 rounded-full font-medium ${isCurrentPlan('premium') ? 'opacity-60 cursor-not-allowed' : 'hover:bg-purple-700'} flex items-center justify-center`}
                disabled={isCurrentPlan('premium') || loadingPremium}
                onClick={handlePremiumSubscribe}
              >
                {!isLoggedIn ? 'Sign In to Subscribe' : 
                 isCurrentPlan('premium') ? 'Your Current Plan' : 
                 isSubscriptionActive ? 'Manage Subscription' : 
                 isTrialMode ? `Try Free for ${trialDays} Day${trialDays === 1 ? '' : 's'}` : 'Subscribe Now'}
                 
                {loadingPremium && <SpinnerIcon />}
              </button>
            </div>
            
            <div className="flex-grow">
              <ul className="space-y-3">
                <li className="flex items-start"><CheckMark />SVG vector format</li>
                <li className="flex items-start"><CheckMark />No attribution required</li>
                <li className="flex items-start"><CheckMark />Icon customization</li>
                <li className="flex items-start"><CheckMark />Full access to all collections</li>
                <li className="flex items-start"><XMark />Unlimited downloads</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Pro Plan (ранее Advanced) */}
        <div className="border rounded-xl flex flex-col">
          <div className="p-6 flex flex-col flex-grow">
            <h2 className="text-4xl font-bold mb-2">Pro</h2>
            <div className="mb-6">
              <span className="text-5xl font-bold">${isYearly ? '29' : '39'}</span>
              <span className="text-purple-600 ml-1">/mo</span>
              <div className="mt-2">
                <p className="text-gray-700">
                  {isYearly ? 'Billed annually ($348/year)' : 'Billed monthly'}
                </p>
              </div>
            </div>
            
            <div className="flex mb-8">
              {/* Pro plan button - для существующих подписок открываем портал, для новых - создаем */}
              <button 
                className={`bg-purple-600 text-white px-6 py-2 rounded-full font-medium ${isCurrentPlan('pro') ? 'opacity-60 cursor-not-allowed' : 'hover:bg-purple-700'} flex items-center justify-center`}
                disabled={isCurrentPlan('pro') || loadingPro}
                onClick={handleProSubscribe}
              >
                {!isLoggedIn ? 'Sign In to Subscribe' : 
                 isCurrentPlan('pro') ? 'Your Current Plan' : 
                 isSubscriptionActive ? 'Manage Subscription' : 
                 isTrialMode ? `Try Free for ${trialDays} Day${trialDays === 1 ? '' : 's'}` : 'Subscribe Now'}
                 
                {loadingPro && <SpinnerIcon />}
              </button>
            </div>
            
            <div className="flex-grow">
              <ul className="space-y-3">
                <li className="flex items-start"><CheckMark />All Premium features</li>
                <li className="flex items-start"><CheckMark />Unlimited icon downloads</li>
                <li className="flex items-start"><CheckMark />Priority support</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      {/* Debug Panel - будет отображаться только в режиме разработки */}
      <DebugPanel 
        isTrialMode={isTrialMode}
        setIsTrialMode={setIsTrialMode}
        trialDays={trialDays}
        setTrialDays={setTrialDays}
        handlePremiumDaily={handlePremiumDaily}
        loadingDaily={loadingDaily}
      />
    </div>
  );
};
 
export default PricingTable;